import React, { useState, useEffect, useCallback } from 'react';
import SystemSettings from './SystemSettings';
import ReferralPage from './ReferralPage';
import FamilyProfilePage from './FamilyProfilePage';
import TopUpCreditsModal from './TopUpCreditsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const YachanguSettingsPage = ({ user, onNavigateToPersonalInfo, onClose, deepLink, onDeepLinkConsumed }) => {
  const [activePage, setActivePage] = useState(null); // null, 'payment', 'promotions', 'safety', etc.
  const [familyInitialTab, setFamilyInitialTab] = useState(null);

  // Honor deep-link: auto-open the requested sub-page (and inner tab if any)
  useEffect(() => {
    if (deepLink?.page) {
      setActivePage(deepLink.page);
      if (deepLink.tab) setFamilyInitialTab(deepLink.tab);
      if (onDeepLinkConsumed) onDeepLinkConsumed();
    }
  }, [deepLink, onDeepLinkConsumed]);
  const settings = [
    {
      icon: '💳',
      iconBg: 'bg-blue-100 dark:bg-blue-900',
      iconColor: 'text-blue-600 dark:text-blue-400',
      label: 'Payment',
      page: 'payment',
      onClick: () => setActivePage('payment')
    },
    {
      icon: '🎟️',
      iconBg: 'bg-orange-100 dark:bg-orange-900',
      iconColor: 'text-orange-600 dark:text-orange-400',
      label: 'Promotions',
      subtitle: 'Enter promo code',
      page: 'promotions',
      onClick: () => setActivePage('promotions')
    },
    {
      icon: '🤝',
      iconBg: 'bg-green-100 dark:bg-green-900',
      iconColor: 'text-green-600 dark:text-green-400',
      label: 'Refer & Earn',
      subtitle: 'Invite friends, earn rewards',
      page: 'referral',
      onClick: () => setActivePage('referral')
    },
    {
      icon: '🛡️',
      iconBg: 'bg-purple-100 dark:bg-purple-900',
      iconColor: 'text-purple-600 dark:text-purple-400',
      label: 'Safety',
      page: 'safety',
      onClick: () => setActivePage('safety')
    },
    {
      icon: '💼',
      iconBg: 'bg-amber-100 dark:bg-amber-900',
      iconColor: 'text-amber-600 dark:text-amber-400',
      label: 'Business Rides',
      page: 'business',
      onClick: () => setActivePage('business')
    },
    {
      icon: '👨‍👩‍👧',
      iconBg: 'bg-rose-100 dark:bg-rose-900',
      iconColor: 'text-rose-600 dark:text-rose-400',
      label: 'Family Profile',
      subtitle: 'Share rides with family members',
      page: 'family',
      onClick: () => setActivePage('family')
    },
    {
      icon: '🎓',
      iconBg: 'bg-teal-100 dark:bg-teal-900',
      iconColor: 'text-teal-600 dark:text-teal-400',
      label: 'Student / School Profile',
      subtitle: 'Verify student status for school rides',
      page: 'student',
      onClick: () => setActivePage('student')
    },
    {
      icon: '💬',
      iconBg: 'bg-cyan-100 dark:bg-cyan-900',
      iconColor: 'text-cyan-600 dark:text-cyan-400',
      label: 'Support',
      page: 'support',
      onClick: () => setActivePage('support')
    },
    {
      icon: 'ℹ️',
      iconBg: 'bg-slate-100 dark:bg-slate-800',
      iconColor: 'text-slate-600 dark:text-slate-400',
      label: 'About',
      page: 'about',
      onClick: () => setActivePage('about')
    },
    {
      icon: '🔔',
      iconBg: 'bg-pink-100 dark:bg-pink-900',
      iconColor: 'text-pink-600 dark:text-pink-400',
      label: 'Notifications',
      page: 'notifications',
      onClick: () => setActivePage('notifications')
    },
    {
      icon: '📅',
      iconBg: 'bg-indigo-100 dark:bg-indigo-900',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
      label: 'Calendar',
      page: 'calendar',
      onClick: () => setActivePage('calendar')
    }
  ];

  // Render individual settings pages
  if (activePage === 'payment') {
    return <PaymentPage onBack={() => setActivePage(null)} user={user} />;
  }
  if (activePage === 'promotions') {
    return <PromotionsPage onBack={() => setActivePage(null)} user={user} />;
  }
  if (activePage === 'referral') {
    return <ReferralPage onBack={() => setActivePage(null)} user={user} />;
  }
  if (activePage === 'safety') {
    return <SafetyPage onBack={() => setActivePage(null)} user={user} />;
  }
  if (activePage === 'business') {
    return <BusinessRidesPage onBack={() => setActivePage(null)} />;
  }
  if (activePage === 'family') {
    return <FamilyProfilePage onBack={() => { setActivePage(null); setFamilyInitialTab(null); }} user={user} initialTab={familyInitialTab} />;
  }
  if (activePage === 'student') {
    return <StudentProfilePage onBack={() => setActivePage(null)} user={user} />;
  }
  if (activePage === 'support') {
    return <SupportPage onBack={() => setActivePage(null)} />;
  }
  if (activePage === 'about') {
    return <AboutPage onBack={() => setActivePage(null)} />;
  }
  if (activePage === 'notifications') {
    return <NotificationsPage onBack={() => setActivePage(null)} user={user} />;
  }
  if (activePage === 'calendar') {
    return <CalendarPage onBack={() => setActivePage(null)} />;
  }

  return (
    <div className="absolute inset-0 bg-green-50 dark:bg-gray-900 z-50 overflow-y-auto transition-colors">
      {/* Header */}
      <div className="p-6 border-b border-green-100 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors">
        <div className="flex items-start space-x-4">
          {/* Profile Picture */}
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-3xl text-green-600 dark:text-green-400">👤</span>
          </div>
          
          {/* User Info */}
          <div className="flex-1 min-w-0">
            {/* Name */}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white truncate mb-2">
              {user?.first_name || user?.firstName || 'User'} {user?.last_name || user?.lastName || ''}
            </h2>
            
            {/* Email */}
            <div className="flex items-center space-x-2 mb-1.5">
              <span className="text-gray-400 dark:text-gray-500">✉️</span>
              <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                {user?.email || 'user@example.com'}
              </span>
            </div>
            
            {/* Phone */}
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-gray-400 dark:text-gray-500">📱</span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {user?.phone || user?.phoneNumber || '+265 999 999 999'}
              </span>
            </div>
            
            {/* Rating */}
            <div className="flex items-center space-x-1 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-lg inline-flex mb-2">
              <span className="text-yellow-500">⭐</span>
              <span className="text-sm font-semibold text-green-700 dark:text-green-400">4.8</span>
            </div>
            
            {/* My Account Button */}
            <button
              onClick={onNavigateToPersonalInfo}
              className="mt-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 text-sm font-medium flex items-center transition-colors"
            >
              <span>My account</span>
              <span className="ml-1">→</span>
            </button>
          </div>
        </div>
      </div>

      {/* Settings List */}
      <div className="p-4 space-y-2">
        {settings.map((setting, index) => (
          <button
            key={index}
            onClick={setting.onClick}
            data-testid={`settings-${setting.page}-btn`}
            className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-2xl transition-all border border-gray-100 dark:border-gray-700"
          >
            <div className="flex items-center space-x-4">
              <div className={`w-14 h-14 ${setting.iconBg} rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors`}>
                <span className="text-2xl">{setting.icon}</span>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900 dark:text-white text-base">
                  {setting.label}
                </div>
                {setting.subtitle && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {setting.subtitle}
                  </div>
                )}
              </div>
            </div>
            <span className="text-gray-400 dark:text-gray-500 text-xl">›</span>
          </button>
        ))}
      </div>

      {/* Divider */}
      <div className="h-3 bg-gray-100 dark:bg-gray-950 transition-colors" />

      {/* System Settings */}
      <div className="px-4 pt-4 pb-2">
        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-2">
          System Settings
        </h3>
      </div>
      <SystemSettings user={user} />

      {/* Become a Driver Banner */}
      <div className="p-4 mt-4 mb-20">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6 rounded-3xl border border-green-100 dark:border-green-800 relative overflow-hidden transition-colors">
          <button className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            ✕
          </button>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Become a driver
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Earn money on your schedule
          </p>
          <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-xl font-semibold text-sm transition-colors">
            Learn more →
          </button>
        </div>
      </div>
    </div>
  );
};

// Payment Page Component
const PaymentPage = ({ onBack, user }) => {
  const [showAddNumber, setShowAddNumber] = React.useState(false);
  const [alternateNumber, setAlternateNumber] = React.useState('');
  const [selectedProvider, setSelectedProvider] = React.useState('airtel');
  const [selectedPayment, setSelectedPayment] = React.useState('mobile_money');
  const [savedNumbers, setSavedNumbers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [walletBalance, setWalletBalance] = React.useState(0);
  const [showTopUpModal, setShowTopUpModal] = React.useState(false);

  // Load saved payment preferences from backend
  React.useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    fetch(`${API_URL}/api/settings/payments/${user.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.preferences) {
          setSavedNumbers(data.preferences.saved_numbers || []);
          setSelectedPayment(data.preferences.selected_payment || 'mobile_money');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    // Load personal wallet balance
    fetch(`${API_URL}/api/credits/${user.id}`)
      .then(r => r.json())
      .then(d => { if (d?.success) setWalletBalance(d.balance || 0); })
      .catch(() => {});
  }, [user?.id]);

  // Save payment preferences to backend
  const savePaymentPrefs = React.useCallback((numbers, payment) => {
    if (!user?.id) return;
    fetch(`${API_URL}/api/settings/payments/${user.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selected_payment: payment, saved_numbers: numbers })
    }).catch(() => {});
  }, [user?.id]);
  
  const MAX_ALTERNATE_NUMBERS = 3;
  const canAddMoreNumbers = savedNumbers.length < MAX_ALTERNATE_NUMBERS;
  
  // Auto-detect provider from phone number
  // Handles all formats: 0991887671, 991887671, +265991887671, +265 99 188 7671
  const detectProvider = (phoneNumber) => {
    if (!phoneNumber) return null;
    const cleaned = phoneNumber.replace(/\D/g, ''); // Remove non-digits
    
    // Normalize: strip country code 265 if present, then ensure we have the local number
    let local = cleaned;
    if (local.startsWith('265') && local.length >= 12) {
      local = local.slice(3); // Remove country code → e.g. 0991887671 or 991887671
    }
    // If 9 digits (no leading 0), prepend 0 → e.g. 991887671 → 0991887671
    if (local.length === 9) {
      local = '0' + local;
    }
    
    // Airtel: 099, 098
    if (local.startsWith('099') || local.startsWith('098')) {
      return 'airtel';
    }
    // TNM Mpamba: 088, 089
    if (local.startsWith('088') || local.startsWith('089')) {
      return 'mpamba';
    }
    return null;
  };
  
  const defaultProvider = detectProvider(user?.phone);
  
  const getProviderInfo = (provider) => {
    if (provider === 'airtel') {
      return {
        name: 'Airtel Money',
        icon: '🔴',
        color: 'red',
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        borderColor: 'border-red-500',
        iconBg: 'bg-red-100 dark:bg-red-900',
        textColor: 'text-red-600 dark:text-red-400',
        prefixes: '099, 098'
      };
    } else if (provider === 'mpamba') {
      return {
        name: 'TNM Mpamba',
        icon: '🟢',
        color: 'green',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        borderColor: 'border-green-500',
        iconBg: 'bg-green-100 dark:bg-green-900',
        textColor: 'text-green-600 dark:text-green-400',
        prefixes: '088, 089'
      };
    }
    return {
      name: 'Mobile Money',
      icon: '📱',
      color: 'gray',
      bgColor: 'bg-gray-50 dark:bg-gray-800',
      borderColor: 'border-gray-300',
      iconBg: 'bg-gray-100 dark:bg-gray-700',
      textColor: 'text-gray-600 dark:text-gray-400',
      prefixes: ''
    };
  };
  
  const handleAddNumber = () => {
    if (alternateNumber && alternateNumber.length >= 9 && canAddMoreNumbers) {
      const detectedProvider = detectProvider(alternateNumber);
      
      // Validation: Check if detected provider matches selected provider
      if (detectedProvider && detectedProvider !== selectedProvider) {
        alert(`Number Mismatch!\n\nThe number you entered (${alternateNumber}) is ${
          detectedProvider === 'airtel' ? 'an Airtel number (099/098)' : 'a TNM Mpamba number (088/089)'
        }.\n\nPlease select the correct provider or enter a different number.`);
        return;
      }
      
      const finalProvider = detectedProvider || selectedProvider;
      const newNumbers = [...savedNumbers, { number: alternateNumber, provider: finalProvider }];
      setSavedNumbers(newNumbers);
      savePaymentPrefs(newNumbers, selectedPayment);
      setAlternateNumber('');
      setShowAddNumber(false);
      setSelectedProvider('airtel');
    }
  };

  const handleRemoveNumber = (index) => {
    const newNumbers = savedNumbers.filter((_, i) => i !== index);
    setSavedNumbers(newNumbers);
    savePaymentPrefs(newNumbers, selectedPayment);
  };

  const handleSelectPayment = (val) => {
    setSelectedPayment(val);
    savePaymentPrefs(savedNumbers, val);
  };
  
  const defaultProviderInfo = getProviderInfo(defaultProvider);
  
  return (
    <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto">
      <div className="p-4 border-b dark:border-gray-700">
        <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2">
          <span>←</span>
          <span>Payment</span>
        </button>
      </div>
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Payment Methods</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">Manage your mobile money accounts</p>

        {/* Personal Wallet (Credits) */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 mb-6" data-testid="personal-wallet-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-green-700 dark:text-green-300 font-semibold">Wallet (Personal Credits)</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300 mt-1" data-testid="personal-wallet-balance">
                K{(walletBalance || 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-green-700/70 dark:text-green-300/80 mt-1">Top up via mobile money. Use at booking.</p>
            </div>
            <button
              onClick={() => setShowTopUpModal(true)}
              data-testid="personal-topup-btn"
              className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg"
            >
              + Add Credits
            </button>
          </div>
        </div>

        {/* Default Mobile Money - User's Registered Number */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Mobile Money</h3>
            {savedNumbers.length > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {savedNumbers.length}/{MAX_ALTERNATE_NUMBERS} alternative numbers
              </span>
            )}
          </div>
          
          {/* Default Number */}
          <button
            onClick={() => handleSelectPayment('mobile_money')}
            className={`w-full p-4 rounded-xl border-2 transition-all ${
              selectedPayment === 'mobile_money'
                ? `${defaultProviderInfo.bgColor} ${defaultProviderInfo.borderColor}`
                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Provider Logo */}
                <div className="w-16 h-16 flex items-center justify-center rounded-xl overflow-hidden bg-white dark:bg-gray-700">
                  {defaultProvider === 'airtel' ? (
                    <>
                      <img 
                        src="/images/payment-logos/airtel-dark.jpg" 
                        alt="Airtel Money"
                        className="w-full h-full object-contain dark:hidden"
                      />
                      <img 
                        src="/images/payment-logos/airtel-light.jpg" 
                        alt="Airtel Money"
                        className="w-full h-full object-contain hidden dark:block"
                      />
                    </>
                  ) : defaultProvider === 'mpamba' ? (
                    <>
                      <img 
                        src="/images/payment-logos/mpamba-dark.jpg" 
                        alt="TNM Mpamba"
                        className="w-full h-full object-contain dark:hidden"
                      />
                      <img 
                        src="/images/payment-logos/mpamba-light.jpg" 
                        alt="TNM Mpamba"
                        className="w-full h-full object-contain hidden dark:block"
                      />
                    </>
                  ) : (
                    <span className="text-3xl">📱</span>
                  )}
                </div>
                
                <div className="text-left">
                  <p className="font-semibold text-gray-900 dark:text-white text-lg">
                    {user?.phone || '+265 999 999 999'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Default • Your registered number</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {selectedPayment === 'mobile_money' && (
                  <span className={`${defaultProviderInfo.textColor} text-xl`}>✓</span>
                )}
              </div>
            </div>
          </button>
          
          {/* Additional Mobile Money Numbers */}
          {savedNumbers.map((item, index) => {
            const providerInfo = getProviderInfo(item.provider);
            return (
              <button
                key={index}
                onClick={() => handleSelectPayment(`mobile_${index}`)}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  selectedPayment === `mobile_${index}`
                    ? `${providerInfo.bgColor} ${providerInfo.borderColor}`
                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Provider Logo */}
                    <div className="w-16 h-16 flex items-center justify-center rounded-xl overflow-hidden bg-white dark:bg-gray-700">
                      {item.provider === 'airtel' ? (
                        <>
                          <img 
                            src="/images/payment-logos/airtel-dark.jpg" 
                            alt="Airtel Money"
                            className="w-full h-full object-contain dark:hidden"
                          />
                          <img 
                            src="/images/payment-logos/airtel-light.jpg" 
                            alt="Airtel Money"
                            className="w-full h-full object-contain hidden dark:block"
                          />
                        </>
                      ) : (
                        <>
                          <img 
                            src="/images/payment-logos/mpamba-dark.jpg" 
                            alt="TNM Mpamba"
                            className="w-full h-full object-contain dark:hidden"
                          />
                          <img 
                            src="/images/payment-logos/mpamba-light.jpg" 
                            alt="TNM Mpamba"
                            className="w-full h-full object-contain hidden dark:block"
                          />
                        </>
                      )}
                    </div>
                    
                    <div className="text-left">
                      <p className="font-semibold text-gray-900 dark:text-white text-lg">{item.number}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Alternative number {index + 1}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {selectedPayment === `mobile_${index}` && (
                      <span className={`${providerInfo.textColor} text-xl`}>✓</span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveNumber(index);
                      }}
                      className="text-red-500 hover:text-red-600 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </button>
            );
          })}
          
          {/* Add Another Number */}
          {canAddMoreNumbers && !showAddNumber && (
            <button
              onClick={() => setShowAddNumber(true)}
              className="w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 hover:border-green-500 dark:hover:border-green-500 hover:text-green-600 dark:hover:text-green-400 transition-colors flex items-center justify-center space-x-2"
            >
              <span className="text-xl">+</span>
              <span className="font-medium">Add another mobile money number</span>
            </button>
          )}
          
          {!canAddMoreNumbers && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-400 text-center">
                Maximum of {MAX_ALTERNATE_NUMBERS} alternative numbers reached
              </p>
            </div>
          )}
          
          {showAddNumber && canAddMoreNumbers && (
            <div className="p-4 border-2 border-green-500 rounded-xl bg-green-50 dark:bg-green-900/20">
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">Add Mobile Money Number</p>
              
              {/* Provider Selection */}
              <div className="mb-3">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">
                  Select Provider
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedProvider('airtel')}
                    className={`relative p-4 rounded-xl border-2 transition-all overflow-hidden ${
                      selectedProvider === 'airtel'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-500 ring-2 ring-red-300 dark:ring-red-600'
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-700'
                    }`}
                  >
                    <div className="relative">
                      {/* Airtel Logo - Light mode (colored background) */}
                      <img 
                        src="/images/payment-logos/airtel-dark.jpg" 
                        alt="Airtel Money"
                        className="w-full h-auto object-contain dark:hidden"
                      />
                      {/* Airtel Logo - Dark mode (white background) */}
                      <img 
                        src="/images/payment-logos/airtel-light.jpg" 
                        alt="Airtel Money"
                        className="w-full h-auto object-contain hidden dark:block"
                      />
                      {/* Checkmark badge */}
                      {selectedProvider === 'airtel' && (
                        <div className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shadow-lg">
                          ✓
                        </div>
                      )}
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setSelectedProvider('mpamba')}
                    className={`relative p-4 rounded-xl border-2 transition-all overflow-hidden ${
                      selectedProvider === 'mpamba'
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-500 ring-2 ring-green-300 dark:ring-green-600'
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-700'
                    }`}
                  >
                    <div className="relative">
                      {/* Mpamba Logo - Light mode (colored background) */}
                      <img 
                        src="/images/payment-logos/mpamba-dark.jpg" 
                        alt="TNM Mpamba"
                        className="w-full h-auto object-contain dark:hidden"
                      />
                      {/* Mpamba Logo - Dark mode (white background) */}
                      <img 
                        src="/images/payment-logos/mpamba-light.jpg" 
                        alt="TNM Mpamba"
                        className="w-full h-auto object-contain hidden dark:block"
                      />
                      {/* Checkmark badge */}
                      {selectedProvider === 'mpamba' && (
                        <div className="absolute -top-2 -right-2 bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shadow-lg">
                          ✓
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              </div>
              
              {/* Number Input */}
              <div className="flex space-x-2">
                <input
                  type="tel"
                  value={alternateNumber}
                  onChange={(e) => setAlternateNumber(e.target.value)}
                  placeholder="+265 999 123 456"
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 outline-none"
                />
                <button
                  onClick={handleAddNumber}
                  disabled={!alternateNumber || alternateNumber.length < 9}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
                >
                  Add
                </button>
              </div>
              
              {/* Auto-detection hint */}
              {alternateNumber.length >= 9 && detectProvider(alternateNumber) && detectProvider(alternateNumber) !== selectedProvider && (
                <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-700 dark:text-red-400 font-medium">
                    ⚠️ Number doesn't match selected provider!
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-500 mt-1">
                    This looks like {getProviderInfo(detectProvider(alternateNumber)).name}.
                    Please select the correct provider or enter a different number.
                  </p>
                </div>
              )}
              
              {alternateNumber.length >= 9 && detectProvider(alternateNumber) && detectProvider(alternateNumber) === selectedProvider && (
                <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-xs text-green-700 dark:text-green-400">
                    ✓ Number matches selected provider
                  </p>
                </div>
              )}
              
              <button
                onClick={() => {
                  setShowAddNumber(false);
                  setAlternateNumber('');
                  setSelectedProvider('airtel');
                }}
                className="mt-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        
        {/* Divider */}
        <div className="border-t dark:border-gray-700 my-6" />
        
        {/* Other Payment Methods */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Other Methods</h3>
          
          <button
            onClick={() => handleSelectPayment('cash')}
            className={`w-full p-4 rounded-xl border-2 transition-all ${
              selectedPayment === 'cash'
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500'
                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">💵</span>
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900 dark:text-white">Cash</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Pay driver directly</p>
                </div>
              </div>
              {selectedPayment === 'cash' && (
                <span className="text-amber-600 dark:text-amber-400 text-xl">✓</span>
              )}
            </div>
          </button>
        </div>
      </div>

      {showTopUpModal && (
        <TopUpCreditsModal
          title="Top up Wallet"
          currentBalance={walletBalance}
          endpoint={`${API_URL}/api/credits/${user?.id}/top-up`}
          phoneSuggestion={user?.phone}
          onSuccess={(data) => {
            setShowTopUpModal(false);
            if (data?.balance !== undefined) setWalletBalance(data.balance);
            // Re-fetch authoritative
            fetch(`${API_URL}/api/credits/${user.id}`).then(r => r.json()).then(d => { if (d?.success) setWalletBalance(d.balance || 0); }).catch(() => {});
          }}
          onClose={() => setShowTopUpModal(false)}
        />
      )}
    </div>
  );
};
const PromotionsPage = ({ onBack, user }) => {
  const [promoCode, setPromoCode] = React.useState('');
  const [userPromos, setUserPromos] = React.useState([]);
  const [availablePromos, setAvailablePromos] = React.useState([]);
  const [autoDiscount, setAutoDiscount] = React.useState(null);
  const [applyMsg, setApplyMsg] = React.useState(null);
  const [applyError, setApplyError] = React.useState(null);

  const userRole = user?.role || 'rider';

  const fetchPromos = React.useCallback(() => {
    if (!user?.id) return;
    fetch(`${API_URL}/api/promo/available/${user.id}?role=${userRole}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setUserPromos(data.user_promos || []);
          setAvailablePromos(data.available_promos || []);
        }
      })
      .catch(() => {});
    fetch(`${API_URL}/api/promo/auto-discount/${user.id}?role=${userRole}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.has_discount) setAutoDiscount(data);
        else setAutoDiscount(null);
      })
      .catch(() => {});
  }, [user?.id, userRole]);

  // Initial fetch + poll every 30s for real-time updates
  React.useEffect(() => {
    fetchPromos();
    const interval = setInterval(fetchPromos, 30000);
    return () => clearInterval(interval);
  }, [fetchPromos]);

  const handleApply = async () => {
    if (!promoCode.trim() || !user?.id) return;
    setApplyMsg(null);
    setApplyError(null);
    try {
      const res = await fetch(`${API_URL}/api/promo/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode, user_id: user.id, role: userRole })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setApplyMsg(data.message);
        setUserPromos(prev => [...prev, data.promo]);
        setPromoCode('');
      } else {
        // Handle error responses (400, 403, 404, etc.)
        const errorMsg = data.detail || data.message || 'Failed to apply code';
        setApplyError(errorMsg);
      }
    } catch (err) {
      console.error('Promo apply error:', err);
      setApplyError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto">
      <div className="p-4 border-b dark:border-gray-700">
        <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2" data-testid="promo-back-btn">
          <span>←</span>
          <span>Promotions</span>
        </button>
      </div>
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Add promo code</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">Enter your promo code to get discounts</p>
        
        <div className="mb-4">
          <input
            type="text"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            placeholder="ENTER CODE"
            data-testid="promo-code-input"
            className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white uppercase"
          />
        </div>

        {applyMsg && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-700 dark:text-green-400">{applyMsg}</p>
          </div>
        )}
        {applyError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-400">{applyError}</p>
          </div>
        )}
        
        <button
          onClick={handleApply}
          disabled={!promoCode.trim()}
          data-testid="apply-promo-btn"
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white py-4 rounded-xl font-semibold transition-colors"
        >
          Apply Code
        </button>

        {autoDiscount && (
          <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200 dark:border-amber-700">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg font-bold text-amber-700 dark:text-amber-400">{autoDiscount.discount_percent}% OFF</span>
              <span className="text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded-full">AUTO</span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {autoDiscount.description || `Automatic discount on your ride #${autoDiscount.ride_number}`}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Applied automatically on your next ride</p>
          </div>
        )}

        {userPromos.length > 0 && (
          <div className="mt-8">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Your Applied Promos</h3>
            <div className="space-y-3">
              {userPromos.map((p, i) => (
                <div key={i} className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <p className="font-semibold text-gray-900 dark:text-white">{p.code}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {p.discount_percent > 0 ? `${p.discount_percent}% off` : ''}
                    {p.discount_amount > 0 ? `MWK ${p.discount_amount} off` : ''}
                    {p.description ? ` - ${p.description}` : ''}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {availablePromos.length > 0 && (
          <div className="mt-8">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Available Promotions</h3>
            <div className="space-y-3">
              {availablePromos.map((p, i) => (
                <div key={i} className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {p.discount_percent > 0 ? `${p.discount_percent}% off` : `MWK ${p.discount_amount} off`} your next ride
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{p.description || `Code: ${p.code}`}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {availablePromos.length === 0 && userPromos.length === 0 && (
          <div className="mt-8 text-center text-gray-500 dark:text-gray-400">
            <p>No promotions available right now</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Safety Page Component
const SafetyPage = ({ onBack, user }) => (
  <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto">
    <div className="p-4 border-b dark:border-gray-700">
      <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2">
        <span>←</span>
        <span>Safety</span>
      </button>
    </div>
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Safety Center</h2>
      
      <div className="space-y-4">
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
          <div className="flex items-start space-x-3">
            <span className="text-2xl">🚨</span>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Emergency Assistance</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Quick access to emergency services</p>
              <button className="text-purple-600 dark:text-purple-400 font-medium text-sm">Call 911</button>
            </div>
          </div>
        </div>
        
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <div className="flex items-start space-x-3">
            <span className="text-2xl">👥</span>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Share Trip Status</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Share your trip with trusted contacts</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <div className="flex items-start space-x-3">
            <span className="text-2xl">📱</span>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">RideCheck</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Automatic trip monitoring</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Business Rides Page Component
const BusinessRidesPage = ({ onBack }) => (
  <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto">
    <div className="p-4 border-b dark:border-gray-700">
      <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2">
        <span>←</span>
        <span>Business Rides</span>
      </button>
    </div>
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Ride Yachangu for Business</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">Manage business travel expenses</p>
      
      <div className="bg-amber-50 dark:bg-amber-900/20 p-6 rounded-xl border border-amber-200 dark:border-amber-800 mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Not set up yet</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Add your business profile to expense rides automatically</p>
        <button className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2.5 rounded-xl font-medium text-sm transition-colors">
          Set up business profile
        </button>
      </div>
      
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-900 dark:text-white">Benefits</h3>
        <div className="flex items-start space-x-3 text-sm">
          <span className="text-green-600 dark:text-green-400">✓</span>
          <p className="text-gray-600 dark:text-gray-400">Automatic expense reports</p>
        </div>
        <div className="flex items-start space-x-3 text-sm">
          <span className="text-green-600 dark:text-green-400">✓</span>
          <p className="text-gray-600 dark:text-gray-400">Centralized billing</p>
        </div>
        <div className="flex items-start space-x-3 text-sm">
          <span className="text-green-600 dark:text-green-400">✓</span>
          <p className="text-gray-600 dark:text-gray-400">Trip management dashboard</p>
        </div>
      </div>
    </div>
  </div>
);


// Student / School Profile Page Component
const StudentProfilePage = ({ onBack, user }) => {
  const [profile, setProfile] = useState({
    school_name: '', student_id: '', grade_level: '', school_address: '', enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    fetch(`${API_URL}/api/settings/student/${user.id}`)
      .then(r => r.json())
      .then(d => {
        if (d?.success && d?.profile) {
          setProfile({
            school_name: d.profile.school_name || '',
            student_id: d.profile.student_id || '',
            grade_level: d.profile.grade_level || '',
            school_address: d.profile.school_address || '',
            enabled: !!d.profile.enabled
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.id]);

  const persist = async (next) => {
    if (!user?.id) return false;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/settings/student/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next)
      });
      return res.ok;
    } catch (_) { return false; }
    finally { setSaving(false); }
  };

  const handleField = (key, value) => {
    setProfile(prev => ({ ...prev, [key]: value }));
  };

  const handleBlurSave = async () => {
    await persist(profile);
  };

  const handleToggleEnabled = async () => {
    if (!profile.enabled && !(profile.school_name.trim() && profile.student_id.trim())) {
      alert('Please add school name and student ID before enabling');
      return;
    }
    const next = { ...profile, enabled: !profile.enabled };
    setProfile(next);
    await persist(next);
  };

  return (
    <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto" data-testid="student-profile-page">
      <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
        <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2">
          <span>←</span>
          <span>Student / School Profile</span>
        </button>
        {saving && <span className="text-[11px] text-gray-500" data-testid="student-saving">Saving…</span>}
      </div>

      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Student Profile</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Verify your student status to access discounted school rides and trusted school routes.
        </p>

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <>
            {/* Enable toggle */}
            <div className="flex items-center justify-between bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-4 mb-5">
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">Student Mode</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Apply student discounts on eligible rides</p>
              </div>
              <button
                onClick={handleToggleEnabled}
                data-testid="student-enable-toggle"
                className={`relative w-12 h-6 rounded-full transition-colors ${profile.enabled ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                aria-pressed={profile.enabled}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${profile.enabled ? 'translate-x-6' : ''}`} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">School / Institution Name</label>
                <input
                  type="text"
                  value={profile.school_name}
                  onChange={e => handleField('school_name', e.target.value)}
                  onBlur={handleBlurSave}
                  placeholder="e.g. University of Malawi"
                  data-testid="student-school-name-input"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Student ID</label>
                <input
                  type="text"
                  value={profile.student_id}
                  onChange={e => handleField('student_id', e.target.value)}
                  onBlur={handleBlurSave}
                  placeholder="e.g. BSC-2024-1234"
                  data-testid="student-id-input"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Grade / Year Level</label>
                <input
                  type="text"
                  value={profile.grade_level}
                  onChange={e => handleField('grade_level', e.target.value)}
                  onBlur={handleBlurSave}
                  placeholder="e.g. Year 3, Form 4, Master's"
                  data-testid="student-grade-input"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">School Address (optional)</label>
                <input
                  type="text"
                  value={profile.school_address}
                  onChange={e => handleField('school_address', e.target.value)}
                  onBlur={handleBlurSave}
                  placeholder="Campus / school address"
                  data-testid="student-address-input"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Benefits</h3>
              <div className="flex items-start space-x-2 text-sm">
                <span className="text-green-600 dark:text-green-400">✓</span>
                <p className="text-gray-600 dark:text-gray-400">Discounted student fares on eligible routes</p>
              </div>
              <div className="flex items-start space-x-2 text-sm">
                <span className="text-green-600 dark:text-green-400">✓</span>
                <p className="text-gray-600 dark:text-gray-400">Save your campus address as a quick destination</p>
              </div>
              <div className="flex items-start space-x-2 text-sm">
                <span className="text-green-600 dark:text-green-400">✓</span>
                <p className="text-gray-600 dark:text-gray-400">Faster trip booking during exam and class hours</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Support Page Component
const SupportPage = ({ onBack }) => (
  <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto">
    <div className="p-4 border-b dark:border-gray-700">
      <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2">
        <span>←</span>
        <span>Support</span>
      </button>
    </div>
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Help & Support</h2>
      
      <div className="space-y-3 mb-6">
        <button className="w-full p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800 text-left hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">💬</span>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">Chat with us</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Get instant help</p>
              </div>
            </div>
            <span className="text-gray-400">›</span>
          </div>
        </button>
        
        <button className="w-full p-4 bg-gray-50 dark:bg-gray-800 rounded-xl text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">📧</span>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">Email Support</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">support@rideyachangu.com</p>
              </div>
            </div>
            <span className="text-gray-400">›</span>
          </div>
        </button>
        
        <button className="w-full p-4 bg-gray-50 dark:bg-gray-800 rounded-xl text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">❓</span>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">FAQ</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Common questions</p>
              </div>
            </div>
            <span className="text-gray-400">›</span>
          </div>
        </button>
      </div>
      
      <div className="border-t dark:border-gray-700 pt-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Quick Help</h3>
        <div className="space-y-2 text-sm">
          <a href="#" className="block text-cyan-600 dark:text-cyan-400 hover:underline">Report a safety issue</a>
          <a href="#" className="block text-cyan-600 dark:text-cyan-400 hover:underline">I lost an item</a>
          <a href="#" className="block text-cyan-600 dark:text-cyan-400 hover:underline">Account and payment</a>
          <a href="#" className="block text-cyan-600 dark:text-cyan-400 hover:underline">A ride issue</a>
        </div>
      </div>
    </div>
  </div>
);

// About Page Component
const AboutPage = ({ onBack }) => (
  <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto">
    <div className="p-4 border-b dark:border-gray-700">
      <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2">
        <span>←</span>
        <span>About</span>
      </button>
    </div>
    <div className="p-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-green-600 rounded-3xl flex items-center justify-center mx-auto mb-4">
          <span className="text-4xl text-white">🚗</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Ride Yachangu</h2>
        <p className="text-gray-600 dark:text-gray-400">Version 1.0.0</p>
      </div>
      
      <div className="space-y-3 mb-6">
        <button className="w-full p-4 bg-gray-50 dark:bg-gray-800 rounded-xl text-left">
          <div className="flex items-center justify-between">
            <span className="text-gray-900 dark:text-white">Terms of Service</span>
            <span className="text-gray-400">›</span>
          </div>
        </button>
        
        <button className="w-full p-4 bg-gray-50 dark:bg-gray-800 rounded-xl text-left">
          <div className="flex items-center justify-between">
            <span className="text-gray-900 dark:text-white">Privacy Policy</span>
            <span className="text-gray-400">›</span>
          </div>
        </button>
        
        <button className="w-full p-4 bg-gray-50 dark:bg-gray-800 rounded-xl text-left">
          <div className="flex items-center justify-between">
            <span className="text-gray-900 dark:text-white">Licenses</span>
            <span className="text-gray-400">›</span>
          </div>
        </button>
      </div>
      
      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
        <p className="mb-2">© 2026 Ride Yachangu</p>
        <p>Your Trusted Ride in Malawi</p>
      </div>
    </div>
  </div>
);

// Notifications Page Component
const NotificationsPage = ({ onBack, user }) => {
  const [settings, setSettings] = React.useState({
    trip_updates: true,
    promos: true,
    news: false,
    email: true,
    sms: false
  });
  const [saved, setSaved] = React.useState(false);
  
  React.useEffect(() => {
    if (!user?.id) return;
    fetch(`${API_URL}/api/settings/notifications/${user.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.preferences) setSettings(data.preferences);
      })
      .catch(() => {});
  }, [user?.id]);

  const toggleSetting = (key) => {
    const updated = { ...settings, [key]: !settings[key] };
    setSettings(updated);
    setSaved(false);
    if (!user?.id) return;
    fetch(`${API_URL}/api/settings/notifications/${user.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updated)
    }).then(() => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }).catch(() => {});
  };
  
  return (
    <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto">
      <div className="p-4 border-b dark:border-gray-700">
        <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2">
          <span>←</span>
          <span>Notifications</span>
        </button>
      </div>
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Notification Settings</h2>
        {saved && (
          <div className="mb-4 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-center">
            <p className="text-xs text-green-700 dark:text-green-400">Preferences saved</p>
          </div>
        )}
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Trip Updates</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Driver arrival, trip status</p>
            </div>
            <button
              onClick={() => toggleSetting('trip_updates')}
              data-testid="toggle-trip-updates"
              className={`w-12 h-6 rounded-full transition-colors ${settings.trip_updates ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings.trip_updates ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Promotions</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Deals and discounts</p>
            </div>
            <button
              onClick={() => toggleSetting('promos')}
              className={`w-12 h-6 rounded-full transition-colors ${settings.promos ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings.promos ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">News & Updates</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">App updates and features</p>
            </div>
            <button
              onClick={() => toggleSetting('news')}
              className={`w-12 h-6 rounded-full transition-colors ${settings.news ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings.news ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          
          <div className="mt-6 pt-6 border-t dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Notification Channels</h3>
            
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl mb-3">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Email</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{user?.email || 'Not set'}</p>
              </div>
              <button
                onClick={() => toggleSetting('email')}
                className={`w-12 h-6 rounded-full transition-colors ${settings.email ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings.email ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">SMS</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{user?.phone || 'Not set'}</p>
              </div>
              <button
                onClick={() => toggleSetting('sms')}
                className={`w-12 h-6 rounded-full transition-colors ${settings.sms ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings.sms ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calendar Page Component
const CalendarPage = ({ onBack }) => (
  <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto">
    <div className="p-4 border-b dark:border-gray-700">
      <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2">
        <span>←</span>
        <span>Calendar</span>
      </button>
    </div>
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Calendar Integration</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">Sync your rides with your calendar</p>
      
      <div className="bg-indigo-50 dark:bg-indigo-900/20 p-6 rounded-xl border border-indigo-200 dark:border-indigo-800 mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Connect Calendar</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Add rides automatically to your calendar</p>
        <div className="space-y-3">
          <button className="w-full p-3 bg-white dark:bg-gray-800 rounded-xl flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <span className="text-2xl">📅</span>
            <span className="font-medium text-gray-900 dark:text-white">Google Calendar</span>
          </button>
          <button className="w-full p-3 bg-white dark:bg-gray-800 rounded-xl flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <span className="text-2xl">📆</span>
            <span className="font-medium text-gray-900 dark:text-white">Apple Calendar</span>
          </button>
        </div>
      </div>
      
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-900 dark:text-white">What gets synced</h3>
        <div className="flex items-start space-x-3 text-sm">
          <span className="text-green-600 dark:text-green-400">✓</span>
          <p className="text-gray-600 dark:text-gray-400">Scheduled rides</p>
        </div>
        <div className="flex items-start space-x-3 text-sm">
          <span className="text-green-600 dark:text-green-400">✓</span>
          <p className="text-gray-600 dark:text-gray-400">Pickup and drop-off times</p>
        </div>
        <div className="flex items-start space-x-3 text-sm">
          <span className="text-green-600 dark:text-green-400">✓</span>
          <p className="text-gray-600 dark:text-gray-400">Location details</p>
        </div>
      </div>
    </div>
  </div>
);

export default YachanguSettingsPage;
